import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Dependency-free per-page SEO. Updates <title>, description, OG/Twitter,
// canonical, hreflang alternates, and <html lang> on route/language change.
// The build-time prerender bakes the same values into each route's static HTML.
//
// Bilingual usage:  usePageSEO({ it: {title, description}, en: {...}, basePath })
// Single-language:  usePageSEO({ title, description, path, noindex })   // e.g. 404
const SITE_URL = 'https://reteitalia.com.au';

const upsertMeta = (key, attr, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, hreflang, href) => {
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export function usePageSEO({ it, en, basePath = '/', title, description, path = '', noindex = false }) {
  const { language } = useLanguage();
  const bilingual = Boolean(it || en);
  const meta = bilingual ? (language === 'en' ? en || it : it || en) : { title, description };
  const itUrl = `${SITE_URL}${basePath === '/' ? '/' : basePath}`;
  const enUrl = basePath === '/' ? `${SITE_URL}/en` : `${SITE_URL}/en${basePath}`;
  const currentUrl = bilingual ? (language === 'en' ? enUrl : itUrl) : `${SITE_URL}${path}`;

  useEffect(() => {
    document.documentElement.lang = bilingual ? language : 'it';
    upsertMeta('robots', 'name', noindex ? 'noindex, follow' : 'index, follow');
    if (meta.title) {
      document.title = meta.title;
      upsertMeta('og:title', 'property', meta.title);
      upsertMeta('twitter:title', 'name', meta.title);
    }
    if (meta.description) {
      upsertMeta('description', 'name', meta.description);
      upsertMeta('og:description', 'property', meta.description);
      upsertMeta('twitter:description', 'name', meta.description);
    }
    upsertMeta('og:url', 'property', currentUrl);
    upsertMeta('twitter:url', 'name', currentUrl);
    if (bilingual) upsertMeta('og:locale', 'property', language === 'en' ? 'en_AU' : 'it_IT');

    upsertLink('canonical', null, currentUrl);
    if (bilingual) {
      upsertLink('alternate', 'it', itUrl);
      upsertLink('alternate', 'en', enUrl);
      upsertLink('alternate', 'x-default', itUrl);
    }
  }, [language, bilingual, noindex, meta.title, meta.description, currentUrl, itUrl, enUrl]);
}

export default usePageSEO;
